<script setup lang="ts">
import {
  compareMoney,
  isZeroMoney,
  type Money,
  moneyProportion,
  signOfMoney,
  sumMoney,
  zeroMoney,
} from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { Card } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

interface Segment {
  readonly key: string
  readonly label: string
  readonly money: Money | undefined
  readonly to: RouteLocationRaw | undefined
}

const props = defineProps<{
  title: string
  direct: Money | undefined
  inSubAccounts: Money | undefined
  segments: readonly Segment[]
}>()

const { t } = useI18n()

const RADIUS = 42
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const GAP = 1.5

const ordered = computed(() =>
  [...props.segments].sort((a, b) => {
    if (a.money === undefined || b.money === undefined) return a.money === undefined ? 1 : -1
    return compareMoney(b.money, a.money)
  }),
)

/** Only a positive figure takes a slice; a refund or a credit still gets its legend row. */
const total = computed(() => {
  const values = props.segments.flatMap((segment) =>
    segment.money !== undefined && signOfMoney(segment.money) > 0 ? [segment.money] : [],
  )
  const first = values[0]
  return first === undefined ? undefined : sumMoney(values, first.currency)
})

function fractionOf(money: Money | undefined): number | undefined {
  if (money === undefined || signOfMoney(money) <= 0 || total.value === undefined) return undefined
  return moneyProportion(money, zeroMoney(total.value.currency), total.value)
}

const rows = computed(() => {
  let offset = 0
  return ordered.value.map((segment, index) => {
    const fraction = fractionOf(segment.money)
    const length = fraction === undefined ? 0 : Math.max(fraction * CIRCUMFERENCE - GAP, 0)
    const row = {
      ...segment,
      percent: fraction === undefined ? undefined : Math.round(fraction * 100),
      swatch: `color-mix(in srgb, var(--color-text) ${Math.max(100 - index * 18, 25)}%, var(--color-surface))`,
      dash: `${length} ${CIRCUMFERENCE - length}`,
      offset: -offset * CIRCUMFERENCE,
      drawn: length > 0,
    }
    offset += fraction ?? 0
    return row
  })
})
</script>

<template>
  <Card class="flex flex-col gap-[var(--space-group)]">
    <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">{{ title }}</h2>

    <dl
      v-if="direct !== undefined && inSubAccounts !== undefined"
      class="grid grid-cols-2 gap-[var(--space-group)]"
    >
      <div class="flex min-w-0 flex-col gap-[var(--space-tight)]">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('accountDetail.subtree.direct') }}
        </dt>
        <dd class="text-[length:var(--text-step-4)] font-semibold tabular-nums">
          <MoneyAmount :value="direct" size="medium" />
        </dd>
      </div>
      <div class="flex min-w-0 flex-col gap-[var(--space-tight)]">
        <dt class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('accountDetail.subtree.inSubAccounts') }}
        </dt>
        <dd class="text-[length:var(--text-step-4)] font-semibold tabular-nums">
          <MoneyAmount :value="inSubAccounts" size="medium" />
        </dd>
      </div>
    </dl>

    <div class="flex flex-wrap items-center gap-[var(--space-block)]">
      <svg
        v-if="total !== undefined && !isZeroMoney(total)"
        width="120"
        height="120"
        viewBox="0 0 112 112"
        aria-hidden="true"
        class="shrink-0"
      >
        <template v-for="row in rows" :key="row.key">
          <circle
            v-if="row.drawn"
            cx="56"
            cy="56"
            :r="RADIUS"
            fill="none"
            :stroke="row.swatch"
            stroke-width="16"
            :stroke-dasharray="row.dash"
            :stroke-dashoffset="row.offset"
            transform="rotate(-90 56 56)"
          />
        </template>
      </svg>

      <ul class="flex min-w-[14rem] flex-1 flex-col gap-[var(--space-inline)]">
        <li
          v-for="row in rows"
          :key="row.key"
          class="grid grid-cols-[0.75rem_minmax(0,1fr)_auto_2.5rem] items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)]"
        >
          <span
            aria-hidden="true"
            class="h-3 w-3 rounded-full"
            :style="{ backgroundColor: row.percent === undefined ? 'transparent' : row.swatch }"
          />
          <RouterLink
            v-if="row.to !== undefined"
            :to="row.to"
            class="truncate text-[color:var(--color-text)] underline-offset-4 hover:underline"
          >
            {{ row.label }}
          </RouterLink>
          <span v-else class="truncate text-[color:var(--color-text)]">{{ row.label }}</span>
          <MoneyAmount v-if="row.money !== undefined" :value="row.money" size="figure" />
          <span v-else />
          <span class="figure text-right text-[color:var(--color-text-muted)]">
            {{ row.percent === undefined ? '' : `${row.percent}%` }}
          </span>
        </li>
      </ul>
    </div>
  </Card>
</template>
